import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  PlayerCardTheme,
  PlayerCardThemeDefault,
  PlayerCardServices,
  Page,
  PlayerCardDefaultables,
} from "../../../services/bedrock";
import { PlayerCardsContextData } from "./PlayerCardsContext";
import { ToastContextData } from "./ToastContext";
import { PaginatedDropdownItemProps } from "../../../components/PaginatedDropdown";
import { CancelToken } from "axios";

export interface ThemesContextData {
  themes: PlayerCardTheme[];
  cachedThemes: { [id: number]: PlayerCardTheme };
  isLoading: boolean;
  activePage: number;
  totalPages: number;
  selectedTheme: PlayerCardTheme | undefined;
  setSelectedTheme: React.Dispatch<React.SetStateAction<PlayerCardTheme | undefined>>;
  inEditMode: boolean;
  setInEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  loadPage: (page?: number) => void;
  saveTheme: (theme: PlayerCardTheme) => Promise<PlayerCardTheme>;
  deleteTheme: (themeToDelete: PlayerCardTheme) => void;
  createDefault: (themeDefaultId: number, segmentationDetailId?: number) => Promise<PlayerCardThemeDefault>;
  updateDefault: (
    themeDefaultId: number,
    themeId: number | null,
    segmentationDetailId?: number
  ) => Promise<PlayerCardThemeDefault>;
  deleteDefault: (defaultId: number) => Promise<void>;
  handleThemeUpdated: (theme: PlayerCardTheme) => void;
  loadThemeDropdownOptions: (
    page: number,
    searchQuery?: string,
    cancelToken?: CancelToken,
    currentRecord?: PlayerCardTheme | null,
    defaultRecord?: PlayerCardTheme | null
  ) => Promise<Page<PaginatedDropdownItemProps>>;
}

export const InitialThemesContextData: ThemesContextData = {
  themes: [],
  cachedThemes: {},
  isLoading: true,
  activePage: 1,
  totalPages: 1,
  selectedTheme: undefined,
  setSelectedTheme: () => {},
  inEditMode: false,
  setInEditMode: () => {},
  loadPage: (_?: number) => {},
  saveTheme: (_: PlayerCardTheme) => Promise.reject("Not implemented"),
  deleteTheme: (_: PlayerCardTheme) => {},
  createDefault: (_: number, __?: number) => Promise.reject("Not implemented"),
  updateDefault: (_: number, __: number | null, ___?: number) => Promise.reject("Not implemented"),
  deleteDefault: (_: number) => Promise.reject("Not implemented"),
  handleThemeUpdated: (_: PlayerCardTheme) => {},
  loadThemeDropdownOptions: (_: number, __?: string, ___?: CancelToken) => Promise.reject("Not implemented"),
};

export const ThemesContext = (Toast: ToastContextData, PlayerCards: PlayerCardsContextData): ThemesContextData => {
  const [themes, setThemes] = useState<PlayerCardTheme[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [selectedTheme, setSelectedTheme] = useState<PlayerCardTheme>();
  const [inEditMode, setInEditMode] = useState(false);
  const [cachedThemes, setCachedThemes] = useState<{ [id: number]: PlayerCardTheme }>({});

  const updateCachedThemes = (themes: PlayerCardTheme[]) => {
    setCachedThemes(prev => {
      const newThemes = { ...prev };
      themes.forEach(theme => {
        newThemes[theme.id] = theme;
      });
      return newThemes;
    });
  };

  const loadPage = useCallback(
    (page?: number) => {
      if (page !== undefined) setActivePage(page);
      else page = activePage;

      setIsLoading(true);
      PlayerCardServices.getThemes(undefined, undefined, page - 1)
        .then(response => {
          updateCachedThemes(response.content);
          const themes = response.content;
          setThemes(themes);
          setTotalPages(response.pagination.totalPages);
          const index = selectedTheme ? response.content.findIndex(t => t.id === selectedTheme.id) : -1;
          if (index !== -1) {
            setSelectedTheme(response.content[index]);
          }
        })
        .catch(console.error)
        .finally(() => setIsLoading(false));
    },
    [activePage]
  );
  useEffect(loadPage, []);

  const saveTheme = async (theme: PlayerCardTheme) => {
    try {
      var updatedTheme: PlayerCardTheme;
      if (theme.id < 0) {
        updatedTheme = await PlayerCardServices.createTheme(theme);
      } else {
        updatedTheme = await PlayerCardServices.updateTheme(theme.id, theme);
      }
      loadPage();
      handleThemeUpdated(theme);
      return updatedTheme;
    } catch (e) {
      Toast.showError(e, "Failed to save theme");
      throw e;
    }
  };

  const deleteTheme = async (themeToDelete: PlayerCardTheme) => {
    try {
      await PlayerCardServices.deleteTheme(themeToDelete.id);
      setThemes(themes => themes.filter(t => t.id !== themeToDelete.id));
    } catch (e) {
      Toast.showError(e, "Failed to delete theme");
      throw e;
    }
  };

  const createDefault = async (themeDefaultId: number, segmentationDetailId?: number) => {
    try {
      const themeDefault = await PlayerCardServices.createThemeDefault(themeDefaultId, segmentationDetailId);
      Toast.showSuccess("Theme default created!");
      return themeDefault;
    } catch (e) {
      Toast.showError(e, "Failed to create theme default");
      throw e;
    }
  };

  const updateDefault = async (themeDefaultId: number, themeId: number | null, segmentationDetailId?: number) => {
    try {
      const themeDefault = await PlayerCardServices.updateThemeDefault(themeDefaultId, themeId, segmentationDetailId);
      Toast.showSuccess("Theme default updated!");
      return themeDefault;
    } catch (e) {
      Toast.showError(e, "Failed to update theme default");
      throw e;
    }
  };

  const deleteDefault = async (defaultId: number) => {
    try {
      await PlayerCardServices.deleteThemeDefault(defaultId);
      Toast.showSuccess("Theme default deleted!");
    } catch (e) {
      Toast.showError(e, "Failed to delete theme default");
      throw e;
    }
  };

  const handleThemeUpdated = (theme: PlayerCardTheme) => {
    PlayerCards.handleThemeUpdated(theme);
    setSelectedTheme(prev => {
      if (prev?.id === theme.id) {
        return theme;
      } else {
        return prev;
      }
    });
    setThemes(prev => {
      const index = prev.findIndex(t => t.id === theme.id);
      if (index >= 0) {
        return [...prev.slice(0, index), theme, ...prev.slice(index + 1)];
      } else {
        return prev;
      }
    });
  };

  const convertToDropdownItems = (
    data: Page<PlayerCardTheme>,
    page: number,
    searchQuery?: string,
    defaultRecord?: PlayerCardTheme | null
  ) => {
    const content: PaginatedDropdownItemProps[] = data.content.map(item => {
      return {
        key: item.id,
        text: item.name,
        value: item.id,
      };
    });
    if (page === 0 && !searchQuery) {
      Object.values(PlayerCardDefaultables).forEach(defaultable => {
        var name = defaultable.name;
        if (defaultable.id === 0 && defaultRecord) name += ` (${defaultRecord.name})`;
        content.unshift({
          key: defaultable.id,
          text: <div className="customDropdownItem">{name}</div>,
          value: defaultable.id,
          searchValues: [],
        });
      });
    }
    return { ...data, content };
  };

  const loadThemeDropdownOptions = async (
    page: number,
    searchQuery?: string,
    cancelToken?: CancelToken,
    currentRecord?: PlayerCardTheme | null,
    defaultRecord?: PlayerCardTheme | null
  ) => {
    const data = await PlayerCardServices.getThemes(searchQuery, -1, page, 25, cancelToken);
    updateCachedThemes(data.content);
    if (currentRecord) data.content.push(currentRecord);
    return convertToDropdownItems(data, page, searchQuery, defaultRecord);
  };

  return {
    themes,
    cachedThemes,
    isLoading,
    activePage,
    totalPages,
    selectedTheme,
    setSelectedTheme,
    inEditMode,
    setInEditMode,
    loadPage,
    saveTheme,
    deleteTheme,
    createDefault,
    updateDefault,
    deleteDefault,
    handleThemeUpdated,
    loadThemeDropdownOptions,
  };
};
